import * as React from 'react'
import { useCallback, useEffect, useState, useMemo } from 'react'

import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect'

export type ScrollDirection = 'horizontal' | 'vertical' | 'both'

// Scroll boundary threshold to prevent false positive scrollability due to rounding errors
const SCROLL_BOUNDARY_THRESHOLD = 1

// Helper functions
const isHorizontalEnabled = (direction: ScrollDirection): boolean =>
  direction === 'horizontal' || direction === 'both'

const isVerticalEnabled = (direction: ScrollDirection): boolean =>
  direction === 'vertical' || direction === 'both'

const getScrollBehavior = (): ScrollBehavior => {
  if (typeof window === 'undefined') return 'auto'
  const mediaQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)')
  return mediaQuery?.matches ? 'auto' : 'smooth'
}

const setupObservers = (
  element: HTMLElement,
  callback: () => void,
): (() => void) => {
  // Set up ResizeObserver to detect element size changes
  const resizeObserver =
    typeof ResizeObserver !== 'undefined' ? new ResizeObserver(callback) : null
  resizeObserver?.observe(element)

  // Set up MutationObserver to detect content changes (added/removed children)
  const mutationObserver =
    typeof MutationObserver !== 'undefined'
      ? new MutationObserver(callback)
      : null
  mutationObserver?.observe(element, {
    childList: true,
    subtree: true,
  })

  // Return cleanup function
  return () => {
    resizeObserver?.disconnect()
    mutationObserver?.disconnect()
  }
}

type ScrollAxis = 'horizontal' | 'vertical'
type ScrollHandlerOptions = {
  element: HTMLElement
  axis: ScrollAxis
  amount: number
  behavior: ScrollBehavior
  direction: 'positive' | 'negative'
}

const performScroll = ({
  element,
  axis,
  amount,
  behavior,
  direction,
}: ScrollHandlerOptions): void => {
  const delta = direction === 'negative' ? -amount : amount

  if (element.scrollBy) {
    const scrollOptions =
      axis === 'horizontal'
        ? { left: delta, behavior }
        : { top: delta, behavior }
    element.scrollBy(scrollOptions)
  } else {
    // Fallback for browsers that don't support scrollBy
    if (axis === 'horizontal') {
      const maxScroll = element.scrollWidth - element.clientWidth
      const newScrollLeft =
        direction === 'negative'
          ? Math.max(0, element.scrollLeft - amount)
          : Math.min(maxScroll, element.scrollLeft + amount)
      element.scrollLeft = newScrollLeft
    } else {
      const maxScroll = element.scrollHeight - element.clientHeight
      const newScrollTop =
        direction === 'negative'
          ? Math.max(0, element.scrollTop - amount)
          : Math.min(maxScroll, element.scrollTop + amount)
      element.scrollTop = newScrollTop
    }
  }
}

export type UseScrollOptions = {
  scrollAmount?: number
  direction?: ScrollDirection
}

export type UseScrollReturn = {
  canScrollLeft: boolean
  canScrollRight: boolean
  scrollLeft: () => void
  scrollRight: () => void
  canScrollUp: boolean
  canScrollDown: boolean
  scrollUp: () => void
  scrollDown: () => void
}

/**
 * A custom React hook that provides horizontal and/or vertical scrolling functionality.
 *
 * @param scrollRef - A ref to the scrollable element
 * @param options - Configuration options for the hook
 * @param options.scrollAmount - Amount to scroll by (default: 200)
 * @param options.direction - Direction(s) to support scrolling (default: 'horizontal')
 * @returns An object containing scroll state and methods for the specified direction(s)
 */
export const useScroll = (
  scrollRef: React.RefObject<HTMLElement>,
  { scrollAmount = 200, direction = 'horizontal' }: UseScrollOptions = {},
): UseScrollReturn => {
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [canScrollUp, setCanScrollUp] = useState(false)
  const [canScrollDown, setCanScrollDown] = useState(false)

  const checkScrollPosition = useCallback((): void => {
    const element = scrollRef.current
    if (!element) {
      setCanScrollLeft(false)
      setCanScrollRight(false)
      setCanScrollUp(false)
      setCanScrollDown(false)
      return
    }

    const {
      scrollLeft,
      scrollWidth,
      clientWidth,
      scrollTop,
      scrollHeight,
      clientHeight,
    } = element

    const horizontalEnabled = isHorizontalEnabled(direction)
    const verticalEnabled = isVerticalEnabled(direction)

    // Update all scroll states at once
    setCanScrollLeft(
      horizontalEnabled && scrollLeft > SCROLL_BOUNDARY_THRESHOLD,
    )
    setCanScrollRight(
      horizontalEnabled &&
        scrollLeft < scrollWidth - clientWidth - SCROLL_BOUNDARY_THRESHOLD,
    )
    setCanScrollUp(verticalEnabled && scrollTop > SCROLL_BOUNDARY_THRESHOLD)
    setCanScrollDown(
      verticalEnabled &&
        scrollTop < scrollHeight - clientHeight - SCROLL_BOUNDARY_THRESHOLD,
    )
  }, [direction, scrollRef])

  // Track scroll behavior and listen for reduced-motion preference changes
  const [behavior, setBehavior] = useState<ScrollBehavior>(() =>
    getScrollBehavior(),
  )

  useEffect(() => {
    const mediaQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    if (!mediaQuery) return

    const handleChange = (e: MediaQueryListEvent) => {
      setBehavior(e.matches ? 'auto' : 'smooth')
    }

    // Use modern addEventListener if available, otherwise fall back to addListener
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
    } else if (mediaQuery.addListener) {
      // Legacy support for older browsers
      mediaQuery.addListener(handleChange)
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange)
      } else if (mediaQuery.removeListener) {
        // Legacy support for older browsers
        mediaQuery.removeListener(handleChange)
      }
    }
  }, [])

  const createScrollHandler = useCallback(
    (axis: ScrollAxis, scrollDirection: 'positive' | 'negative') =>
      (): void => {
        const element = scrollRef.current
        const axisEnabled =
          axis === 'horizontal'
            ? isHorizontalEnabled(direction)
            : isVerticalEnabled(direction)

        if (!element || !axisEnabled) return

        performScroll({
          element,
          axis,
          amount: scrollAmount,
          behavior,
          direction: scrollDirection,
        })
      },
    [scrollRef, direction, scrollAmount, behavior],
  )

  const scrollLeft = useMemo(
    () => createScrollHandler('horizontal', 'negative'),
    [createScrollHandler],
  )
  const scrollRight = useMemo(
    () => createScrollHandler('horizontal', 'positive'),
    [createScrollHandler],
  )
  const scrollUp = useMemo(
    () => createScrollHandler('vertical', 'negative'),
    [createScrollHandler],
  )
  const scrollDown = useMemo(
    () => createScrollHandler('vertical', 'positive'),
    [createScrollHandler],
  )

  // Check initial scroll position immediately after render
  useIsomorphicLayoutEffect(() => {
    checkScrollPosition()
  }, [checkScrollPosition])

  useEffect(() => {
    const element = scrollRef.current
    if (!element) return

    // Set up event listeners
    element.addEventListener('scroll', checkScrollPosition, { passive: true })
    window.addEventListener('resize', checkScrollPosition, { passive: true })

    // Set up observers
    const cleanupObservers = setupObservers(element, checkScrollPosition)

    return (): void => {
      element.removeEventListener('scroll', checkScrollPosition)
      window.removeEventListener('resize', checkScrollPosition)
      cleanupObservers()
    }
  }, [scrollRef, checkScrollPosition])

  return React.useMemo(
    () => ({
      canScrollLeft,
      canScrollRight,
      scrollLeft,
      scrollRight,
      canScrollUp,
      canScrollDown,
      scrollUp,
      scrollDown,
    }),
    [
      canScrollLeft,
      canScrollRight,
      scrollLeft,
      scrollRight,
      canScrollUp,
      canScrollDown,
      scrollUp,
      scrollDown,
    ],
  )
}
